// React hooks for state, effects, memoization, and stable refs
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Centrifugo WebSocket client and auth error type
import {
  Centrifuge,
  UnauthorizedError,
  type Subscription,
} from 'centrifuge';

// WebSocket endpoint URL from app config
import { webSocketUrl } from '@/app/common/api/config';

// RTK Query lazy endpoints for notifications REST API and Centrifugo token
import {
  useLazyGetNotifQuery,
  useLazyGetCentrifugoTokenQuery,
} from '@/app/common/api/notifications/inappApi';

// API response types for token payload and notification rows
import type {
  GenerateCentrifugoTokenData,
  NotificationRow,
} from '@/app/common/api/notifications/types/inappApiTypes';

// Logical channel keys used in UI and API requests
type NotificationChannel = 'public' | 'private';

// Notification list grouped by channel
type NotificationsByChannel = Record<
  NotificationChannel,
  NotificationRow[]
>;

// Current pagination page per channel
type ChannelPageState = Record<NotificationChannel, number>;
// Boolean flags per channel (hasMore, isFetching)
type ChannelBooleanState = Record<NotificationChannel, boolean>;
// Unread count per channel
type UnreadByChannel = Record<NotificationChannel, number>;

// Shape of a Centrifugo publication event payload
interface PublicationContext {
  data?: NotificationRow;
}

// Hook input parameters
export interface UseNotificationsParams {
  appName: string;
  enabled?: boolean;
  onUnauthorized?: () => void;
}

// Number of notifications fetched per REST page
const PER_PAGE = 10;

// Returns true when an RTK Query error indicates auth failure
function isApiAuthError(err: unknown): boolean {
  const status = (err as { status?: number })?.status;
  return status === 401 || status === 403;
}

/**
 * Manages in-app notifications: REST pagination + real-time updates via Centrifugo.
 */
export function useNotifications({
  appName,
  enabled = true,
  onUnauthorized,
}: UseNotificationsParams) {
  // Active Centrifugo client instance
  const centrifugeRef = useRef<Centrifuge | null>(null);
  // Map of channel name -> subscription object
  const subscriptionsRef = useRef<Record<string, Subscription>>({});
  // Prevents reconnect/token refresh during intentional cleanup
  const isUnmountingRef = useRef(false);

  // Stable ref so event handlers always call the latest callback
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  // Loaded notification rows for each channel
  const [notifications, setNotifications] =
    useState<NotificationsByChannel>({
      public: [],
      private: [],
    });

  // Server-reported unread counts per channel
  const [countUnreadByChannel, setCountUnreadByChannel] =
    useState<UnreadByChannel>({
      public: 0,
      private: 0,
    });

  // Current page index per channel for infinite scroll
  const [page, setPage] = useState<ChannelPageState>({
    public: 1,
    private: 1,
  });

  // Whether more pages exist per channel
  const [hasMore, setHasMore] = useState<ChannelBooleanState>({
    public: true,
    private: true,
  });

  // Loading state for pagination requests per channel
  const [isFetching, setIsFetching] = useState<ChannelBooleanState>({
    public: false,
    private: false,
  });

  // Lazy RTK Query triggers (invoked manually, not on mount)
  const [getNotif] = useLazyGetNotifQuery();
  const [getCentrifugoToken] = useLazyGetCentrifugoTokenQuery();

  // Keep latest token query trigger without re-running the init effect
  const getCentrifugoTokenRef = useRef(getCentrifugoToken);
  getCentrifugoTokenRef.current = getCentrifugoToken;

  /**
   * Fetches full Centrifugo token response (JWT + channel names) from the backend.
   */
  const fetchTokenResponse = useCallback(async (): Promise<GenerateCentrifugoTokenData> => {
    // Abort token fetch if the hook is cleaning up
    if (isUnmountingRef.current) {
      throw new Error('component unmounting');
    }

    // POST /generate_token
    const response = await getCentrifugoTokenRef.current().unwrap();

    // Reject when backend returns an empty payload
    if (!response?.token) {
      throw new Error('centrifugo token missing');
    }

    return response;
  }, []);

  // Stable ref for use inside Centrifugo getToken callback and init effect
  const fetchTokenResponseRef = useRef(fetchTokenResponse);
  fetchTokenResponseRef.current = fetchTokenResponse;

  /**
   * Centrifugo getToken callback — called automatically when the JWT expires.
   */
  const getConnectionToken = useCallback(async () => {
    try {
      const response = await fetchTokenResponseRef.current();
      return response.token;
    } catch (err: unknown) {
      // Stop Centrifugo retries and notify caller on auth errors
      if (isApiAuthError(err)) {
        onUnauthorizedRef.current?.();
        throw new UnauthorizedError('');
      }
      // Transient errors are re-thrown so Centrifugo retries with backoff
      throw err;
    }
  }, []);

  const getConnectionTokenRef = useRef(getConnectionToken);
  getConnectionTokenRef.current = getConnectionToken;

  /**
   * Unsubscribes and removes all active Centrifugo channel subscriptions.
   */
  const teardownSubscriptions = useCallback(() => {
    const centrifuge = centrifugeRef.current;

    Object.values(subscriptionsRef.current).forEach((sub) => {
      try {
        sub.unsubscribe();
        centrifuge?.removeSubscription(sub);
      } catch {
        // Ignore errors during cleanup
      }
    });

    subscriptionsRef.current = {};
  }, []);

  /**
   * Loads a page of notifications from the REST API and updates local state.
   */
  const loadNotifications = useCallback(
    async (channel: NotificationChannel, nextPage: number) => {
      // POST /get_notif_v2
      const response = await getNotif({
        data: {
          channel,
          page: nextPage,
          perPage: PER_PAGE,
          app_name: appName,
        },
      }).unwrap();

      const rows = response?.rows ?? [];
      const unread = response?.count_unread_msg ?? 0;

      // Sync unread badge with server count
      setCountUnreadByChannel((prev) => ({
        ...prev,
        [channel]: unread,
      }));

      // Replace list on page 1, append on subsequent pages
      setNotifications((prev) => ({
        ...prev,
        [channel]:
          nextPage === 1 ? rows : [...prev[channel], ...rows],
      }));

      setPage((prev) => ({
        ...prev,
        [channel]: nextPage,
      }));

      // Full page means there may be more data
      setHasMore((prev) => ({
        ...prev,
        [channel]: rows.length >= PER_PAGE,
      }));

      return response;
    },
    [appName, getNotif],
  );

  // Stable ref so init effect does not depend on loadNotifications identity
  const loadNotificationsRef = useRef(loadNotifications);
  loadNotificationsRef.current = loadNotifications;

  /**
   * Creates and subscribes to a Centrifugo channel (once per channel name).
   */
  const createSubscription = useCallback(
    (
      centrifuge: Centrifuge,
      logical: NotificationChannel,
      channelName: string,
    ) => {
      // Skip if already subscribed to this channel
      if (subscriptionsRef.current[channelName]) {
        return;
      }

      const sub = centrifuge.newSubscription(channelName);

      // Real-time notification received on this channel
      sub.on('publication', (ctx: PublicationContext) => {
        const notif = ctx?.data;
        if (!notif) return;

        // Prepend new notification to the list
        setNotifications((prev) => ({
          ...prev,
          [logical]: [notif, ...prev[logical]],
        }));

        // Increment local unread count
        setCountUnreadByChannel((prev) => ({
          ...prev,
          [logical]: prev[logical] + 1,
        }));
      });

      sub.on('error', (ctx) => {
        console.error(`[${logical}] subscription error`, ctx);
      });

      sub.subscribe();
      subscriptionsRef.current[channelName] = sub;
    },
    [],
  );

  /**
   * One-time setup: fetch token, load initial notifications, connect WebSocket.
   */
  useEffect(() => {
    // Wait until the user is authenticated (enabled is tied to access token)
    if (!enabled || !appName) {
      return;
    }

    let mounted = true;
    isUnmountingRef.current = false;

    async function init() {
      try {
        // Get JWT and channel names from backend
        const tokenResponse = await fetchTokenResponseRef.current();

        // Load first page for both channels via REST
        await loadNotificationsRef.current('public', 1);
        await loadNotificationsRef.current('private', 1);

        // Avoid state updates if the component unmounted during async work
        if (!mounted) {
          return;
        }

        const channelInfo = tokenResponse.centrifugo;

        // Create Centrifugo client with initial token and auto-refresh callback
        const centrifuge = new Centrifuge(webSocketUrl, {
          token: tokenResponse.token,
          getToken: (_ctx) => getConnectionTokenRef.current(),
        });

        centrifugeRef.current = centrifuge;

        centrifuge.on('connected', () => {
          console.log('socket connected');
        });

        centrifuge.on('disconnected', (ctx) => {
          console.log('socket disconnected', ctx);

          if (isUnmountingRef.current) {
            return;
          }

          // 4001 = unauthorized disconnect
          if (ctx.code === 4001) {
            onUnauthorizedRef.current?.();
          }
        });

        centrifuge.on('error', (ctx) => {
          console.error('socket error', ctx);
        });

        // Channels to subscribe — private comes from API, public has a fallback name
        const subscriptions: Array<{
          logical: NotificationChannel;
          channelName: string | undefined;
        }> = [
          { logical: 'private', channelName: channelInfo?.private },
          { logical: 'public', channelName: channelInfo?.public ?? `public:${appName}` },
        ];

        for (const { logical, channelName } of subscriptions) {
          if (channelName) {
            createSubscription(centrifuge, logical, channelName);
          }
        }

        centrifuge.connect();
      } catch (err) {
        console.error('notification init failed', err);
        onUnauthorizedRef.current?.();
      }
    }

    void init();

    // Cleanup on unmount or when enabled/appName changes
    return () => {
      mounted = false;
      isUnmountingRef.current = true;
      teardownSubscriptions();
      centrifugeRef.current?.disconnect();
      centrifugeRef.current = null;
    };
  }, [appName, enabled, createSubscription, teardownSubscriptions]);

  /**
   * Loads the next page for infinite scroll in the notification dropdown.
   */
  const fetchNextPage = useCallback(
    async (channel: NotificationChannel) => {
      // Guard against duplicate or unnecessary requests
      if (isFetching[channel] || !hasMore[channel]) {
        return;
      }

      setIsFetching((prev) => ({
        ...prev,
        [channel]: true,
      }));

      try {
        await loadNotifications(channel, page[channel] + 1);
      } finally {
        setIsFetching((prev) => ({
          ...prev,
          [channel]: false,
        }));
      }
    },
    [hasMore, isFetching, loadNotifications, page],
  );

  // Total unread count across both channels for the navbar badge
  const countUnreadMsg = useMemo(() => {
    return countUnreadByChannel.public + countUnreadByChannel.private;
  }, [countUnreadByChannel]);

  return {
    notifications,
    countUnreadMsg,
    countUnreadByChannel,
    fetchNextPage,
    hasMore,
    isFetching,
    setNotifications,
    setCountUnreadByChannel,
  };
}
